import React from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { RQ_DEPLOY_SUMMARY } from 'Utils';
import { Form, Formik } from 'formik';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import { getDeploymentSummary } from 'Services';
import { Modal, Textarea, Loading } from '@brighterion/br-ui';
import useTemplateConfig from 'Hooks/query-hooks/useTemplateConfig';
import useMutateDeploy from 'Hooks/query-hooks/deployment/useMutateDeploy';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import reducer, { DeployModalConfirmReducer, updateState, fetchingState } from './reducer';
import DeployModalLoading from '../DeployModalLoading';
import DeployCount from '../DeployCount';

import styles from '../styles.css';

interface Props {
    closeModal(): void;
    deploymentSummary: DeploymentSummary;
    rulesType?: RulesType;
}

const DeployModalConfirm = ({ closeModal, deploymentSummary, rulesType }: Props) => {
    const initialState: DeployModalConfirmReducer = {
        previousDeploymentSummary: deploymentSummary,
        newDeploymentSummary: deploymentSummary,
        isCheckingNew: false
    };
    /** Get cache Template config */
    const templateConfig = useTemplateConfig();
    const queryClient = useQueryClient();

    const { t } = useTranslation();
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const { previousDeploymentSummary, newDeploymentSummary, isCheckingNew } = state;
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const deployHeaderText = t('The following updates will be deployed');
    const mutation = useMutateDeploy(templateRepositoryId!, {});
    const { isLoading } = mutation;

    const handleCancelModal = () => {
        switch (rulesType) {
            case 'globalRules': {
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'Review Deployment - Cancel',
                        text: 'Cancel Global Rules Deployment'
                    }
                });
                break;
            }
            case 'merchantOverrides': {
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'Review Deployment - Cancel',
                        text: 'Cancel Subject Deployment'
                    }
                });
                break;
            }
            default: {
                break;
            }
        }
        closeModal();
    };

    const handleTrackEventClick = () => {
        switch (rulesType) {
            case 'globalRules': {
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'Review Deployment - Deploy',
                        text: 'Deploy Global Rules Sandbox'
                    }
                });
                break;
            }
            case 'merchantOverrides': {
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'Review Deployment - Deploy',
                        text: 'Deploy Subject'
                    }
                });
                break;
            }
            default: {
                break;
            }
        }
    };

    /** This will never be triggered, because Template config is cache on the First Page load */
    if (templateConfig.isLoading || templateConfig.isFetching) return null;

    return (
        <Formik
            enableReinitialize
            initialValues={{
                comment: '',
                templateRepositoryId: templateRepositoryId!
            }}
            onSubmit={async (formValues) => {
                dispatch(fetchingState());
                const newSummary = await getDeploymentSummary(templateRepositoryId!);

                if (_.isEqual(deploymentSummary, newSummary)) {
                    mutation.mutate(formValues, { onSettled: () => closeModal() });
                } else {
                    dispatch(
                        updateState({
                            previousDeploymentSummary: _.omit(deploymentSummary),
                            newDeploymentSummary: newSummary
                        })
                    );
                    queryClient.setQueryData([RQ_DEPLOY_SUMMARY], newSummary);
                }
                handleTrackEventClick();
            }}
        >
            {({ handleChange, values, handleSubmit }) => {
                if (isCheckingNew || !templateConfig.data) {
                    return <DeployModalLoading closeModal={closeModal} />;
                }

                return (
                    <Form>
                        <Modal.Title
                            id='deploy-modal-title'
                            title={t('Review Deployment')}
                            onClose={closeModal}
                            showCloseIcon={false}
                            statusIcon='warning'
                        />
                        <div className={styles.modalContent}>
                            {!_.isEqual(previousDeploymentSummary, newDeploymentSummary) && (
                                <div className={styles.newUpdates}>{t('New updates found')}</div>
                            )}
                            <p className={styles.deployText}>{deployHeaderText}</p>
                            <DeployCount
                                prevDeploymentSummary={previousDeploymentSummary}
                                newDeploymentSummary={newDeploymentSummary}
                            />
                            <Textarea
                                label={t('Comment (Optional)')}
                                lineNum={4}
                                showCounter={false}
                                onChange={handleChange}
                                maxChar={5000}
                                value={values.comment}
                                name='comment'
                                allowPan
                            />
                        </div>
                        <Modal.Footer
                            id='deploy-footer'
                            onActionText={isLoading ? <Loading size={28} fillName='white' /> : t('Deploy')}
                            disableOnAction={isLoading}
                            onClose={handleCancelModal}
                            onAction={handleSubmit}
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default DeployModalConfirm;
